import React, { useState, useEffect, useCallback } from "react";
import { Table, Tag, Card, Row, Col, Statistic, Input, Button, Space, message, Typography, Select } from "antd";
import { SearchOutlined, EyeOutlined, ReloadOutlined, RobotOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, BankOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { API_CONST } from "../../../const";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const PageWrapper = styled.div`
  min-height: 100vh;

  .stats-row {
    margin-bottom: 24px;
  }

  .stats-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .search-section {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .table-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .clickable-row {
    cursor: pointer;
  }

  .clickable-row:hover {
    background-color: #f5f5f5;
  }

  .org-link {
    color: #1890ff;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const AdminAssessments = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, completed: 0, processing: 0, failed: 0 });
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.GET_AI_PROJECT_ASSESSMENTS, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.success) {
        setAssessments(data.assessments || []);
      }
    } catch (error) {
      message.error("Failed to fetch assessments");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.GET_AI_ASSESSMENT_STATS, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchAssessments();
    fetchStats();
  }, [fetchAssessments, fetchStats]);

  const getStatusTag = (status) => {
    const statusConfig = {
      completed: { color: "success", icon: <CheckCircleOutlined />, text: "Completed" },
      processing: { color: "processing", icon: <ClockCircleOutlined />, text: "Processing" },
      failed: { color: "error", icon: <ExclamationCircleOutlined />, text: "Failed" },
      pending: { color: "default", icon: <ClockCircleOutlined />, text: "Pending" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: "Assessment ID",
      dataIndex: "id",
      key: "id",
      width: 180,
      render: (id) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{id}</span>,
    },
    {
      title: "Organization",
      dataIndex: "customerOrganization",
      key: "customerOrganization",
      width: 150,
      render: (org) => (
        <span 
          className="org-link" 
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/organization/${org}`);
          }}
        >
          <BankOutlined style={{ marginRight: 4 }} />
          {org}
        </span>
      ),
    },
    {
      title: "Created By",
      dataIndex: "userName",
      key: "userName",
      width: 150,
    },
    {
      title: "Description",
      dataIndex: "requirementDescription",
      key: "requirementDescription",
      ellipsis: true,
      render: (text) => text?.substring(0, 80) + (text?.length > 80 ? "..." : ""),
    },
    {
      title: "Technologies",
      dataIndex: "technologies",
      key: "technologies",
      width: 180,
      render: (techs) => (
        <Space wrap size={[4, 4]}>
          {(techs || []).slice(0, 2).map((tech, i) => (
            <Tag key={i} color="blue">{tech}</Tag>
          ))}
          {techs?.length > 2 && <Tag>+{techs.length - 2}</Tag>}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: (date) => dayjs(date).format("MMM DD, YYYY"),
    },
    {
      title: "Action",
      key: "action",
      width: 90,
      render: (_, record) => (
        <Button 
          type="link" 
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/assessments/${record.id}`);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const filteredAssessments = assessments.filter((a) => {
    const searchLower = searchText.toLowerCase();
    const matchesSearch = 
      a.id?.toLowerCase().includes(searchLower) ||
      a.requirementDescription?.toLowerCase().includes(searchLower) ||
      a.customerOrganization?.toLowerCase().includes(searchLower) ||
      a.userName?.toLowerCase().includes(searchLower) ||
      (a.technologies || []).some((t) => t.toLowerCase().includes(searchLower));
    
    const matchesStatus = !statusFilter || a.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <PageWrapper>
      <PageHeader><h1>Assessments</h1><p>Review and manage AI project assessments</p></PageHeader>

      <Row gutter={16} className="stats-row">
        <Col span={6}>
          <Card className="stats-card">
            <Statistic title="Total Assessments" value={stats.total} prefix={<RobotOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stats-card">
            <Statistic title="Completed" value={stats.completed} valueStyle={{ color: "#52c41a" }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stats-card">
            <Statistic title="Processing" value={stats.processing} valueStyle={{ color: "#1890ff" }} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stats-card">
            <Statistic title="Failed" value={stats.failed} valueStyle={{ color: "#ff4d4f" }} prefix={<ExclamationCircleOutlined />} />
          </Card>
        </Col>
      </Row>

      <Card className="table-card">
        <div className="search-section">
          <Input
            placeholder="Search by ID, organization, user, description, or technology..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 400 }}
            allowClear
          />
          <Select
            placeholder="Filter by status"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="completed">Completed</Option>
            <Option value="processing">Processing</Option>
            <Option value="failed">Failed</Option>
            <Option value="pending">Pending</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchAssessments(); fetchStats(); }}>
            Refresh
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={filteredAssessments}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
          onRow={(record) => ({
            onClick: () => navigate(`/admin/assessments/${record.id}`),
            className: "clickable-row",
          })}
        />
      </Card>
    </PageWrapper>
  );
};

export default AdminAssessments;
